import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CardMedia, Input, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useGetCategoriesQuery, useAddVehicleTypeMutation, useUpdateVehicleTypeMutation, useUploadMediaMutation } from "../service/features/api";
import { toast } from "react-toastify";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  vehicleTypeData?: any;
}

export default function AddVehicleType({
  open,
  onClose,
  setOpen,
  vehicleTypeData
}: ModalProps) {

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100 });
  const [addVehicleType, { isLoading: isAdding }] = useAddVehicleTypeMutation();
  const [updateVehicleType, { isLoading: isUpdating }] = useUpdateVehicleTypeMutation();
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const [name, setName] = useState<string>("");
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (vehicleTypeData && open) {
      setName(vehicleTypeData.name || "");
      let catIds: string[] = [];
      if (vehicleTypeData.categoryId) {
        catIds = Array.isArray(vehicleTypeData.categoryId) ? vehicleTypeData.categoryId : [vehicleTypeData.categoryId];
      } else if (vehicleTypeData.category) {
        catIds = vehicleTypeData.category.map((c: any) => c._id);
      }
      setSelectedCategories(catIds);
      const vType = vehicleTypeData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setImage(vehicleTypeData.image || "");
      setErrors({});
    } else if (!vehicleTypeData && open) {
      // Reset form
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setImage("");
      setErrors({});
      setFileName("");
    }
  }, [vehicleTypeData, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    if (e.target.value.length <= 40) {
      setName(e.target.value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Name is required";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required";
      isValid = false;
    }
    if (!image) {
      tempErrors.image = "Image is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      if (!file.type.includes("image")) {
        toast.error("Please upload a valid image file");
        return;
      }

      setFileName(file.name);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadMedia(formData).unwrap();
        const uploadedUrl = res?.data?.url || res?.url || res?.data;
        if (uploadedUrl) {
          setImage(uploadedUrl);
          if (errors.image) setErrors({ ...errors, image: "" });
          toast.success("Image uploaded successfully");
        }
      } catch (err: any) {
        toast.error("Failed to upload image");
      }
    }
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    const payload = {
      name,
      category: selectedCategories,
      type: Number(selectedVehicleType),
      image
    };

    try {
      if (vehicleTypeData?._id) {
        await updateVehicleType({ id: vehicleTypeData._id, ...payload }).unwrap();
        toast.success("Vehicle Type updated successfully");
      } else {
        await addVehicleType(payload).unwrap();
        toast.success("Vehicle Type added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{vehicleTypeData ? "Edit Vehicle Type" : "Add Vehicle Type"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="control_group w_50">
              <label htmlFor="upload-category-image">
                Upload Image<span className="req">*</span>
              </label>

              {isUploading ? (
                <div className="upload_image no_radius" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress size={24} />
                </div>
              ) : image ? (
                <div className="upload_image no_radius">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="Category" />
                    </figure>
                    <CloseIcon
                      className="remove_img"
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image no_radius"
                  htmlFor="upload-category-image"
                >
                  <Input
                    sx={{ display: "none" }}
                    id="upload-category-image"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageUpload}
                    disabled={isUploading}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/placeholder.png"
                        alt="Upload"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="Edit"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
              {errors.image && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.image}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="vehicle-type-name">Vehicle Type Name</label>
              <TextField
                id="vehicle-type-name"
                placeholder="Enter Name"
                fullWidth
                hiddenLabel
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category</label>
              <Select
                multiple
                fullWidth
                hiddenLabel
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  const selectedIds = selected as string[];
                  if (selectedIds.length === 0) return "Select Categories";

                  return selectedIds.map(id =>
                    categoriesData?.data?.data?.find((cat: any) => cat._id === id)?.name
                  ).filter(Boolean).join(", ");
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.category}
              >
                <MenuItem value="" disabled>Select Categories</MenuItem>
                {categoriesData?.data?.data?.map((category: any) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="VehicleType">Vehicle Type</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                value={selectedVehicleType}
                onChange={handleVehicleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
